/**
 * Owner: Masa Bayu
 * File: pdf-image-object.ts
 * Date:Time: 2026-09-30 13:20
 * Description: One uncompressed PDF image into existing Document IR layout fields.
 */

import type { QXDocumentIR } from '@qx/qx-document-ir';
import { parsePdfLayoutToIR } from './pdf-layout-parse';

export type QXPdfImagePlacement = {
  readonly sourceObjectId: string;
  readonly assetId: string;
  readonly pageNumber: number;
  readonly paintIndex: number;
  readonly rotation: 0;
  readonly placed: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly jpeg: Uint8Array;
};

export type QXPdfImageImport = QXPdfImagePlacement & {
  readonly document: QXDocumentIR;
};

type Hit = {
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly x: number;
  readonly pdfY: number;
  readonly paintIndex: number;
};

const latin1 = new TextDecoder('latin1');

function bytesOf(text: string): Uint8Array {
  return Uint8Array.from(text, (char) => char.charCodeAt(0));
}

function indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  const last = haystack.length - needle.length;
  for (let index = from; index <= last; index += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[index + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return index;
  }
  return -1;
}

function textOf(file: Uint8Array, start: number, end: number): string {
  return latin1.decode(file.subarray(start, end));
}

function objectSlice(file: Uint8Array, id: number): Uint8Array {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  const at = marked >= 0 ? marked + 1 : indexOfBytes(file, bytesOf(`${String(id)} 0 obj`)) === 0 ? 0 : -1;
  if (at < 0) throw new Error(`PDF object ${String(id)} is missing`);
  return file.subarray(at);
}

function readStream(file: Uint8Array, id: number): { readonly dict: string; readonly stream: Uint8Array | null } {
  const slice = objectSlice(file, id);
  const streamAt = indexOfBytes(slice, bytesOf('stream'));
  const endAt = indexOfBytes(slice, bytesOf('endobj'));
  if (streamAt < 0 || (endAt >= 0 && endAt < streamAt)) {
    return { dict: textOf(slice, 0, endAt < 0 ? slice.length : endAt), stream: null };
  }
  const dict = textOf(slice, 0, streamAt);
  const length = dict.match(/\/Length\s+(\d+)/);
  if (!length?.[1]) throw new Error(`PDF object ${String(id)} stream has no length`);
  let dataAt = streamAt + 'stream'.length;
  if (slice[dataAt] === 0x0d) dataAt += 1;
  if (slice[dataAt] === 0x0a) dataAt += 1;
  const size = Number(length[1]);
  return { dict, stream: slice.subarray(dataAt, dataAt + size) };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index] ?? 0);
  return btoa(binary);
}

function placements(content: string): Hit[] {
  const pattern = /([\d.]+)\s+0\s+0\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+cm\s*\/(Image\d+)\s+Do/g;
  const hits: Hit[] = [];
  for (const item of content.matchAll(pattern)) {
    hits.push({
      name: item[5] ?? '',
      width: Number(item[1]),
      height: Number(item[2]),
      x: Number(item[3]),
      pdfY: Number(item[4]),
      paintIndex: hits.length,
    });
  }
  return hits;
}

function clearImage(hits: readonly Hit[], pageWidth: number, pageHeight: number): Hit {
  const pageArea = pageWidth * pageHeight;
  const candidates = hits.filter(
    (hit) => hit.width >= 80 && hit.height >= 80 && hit.width * hit.height < pageArea * 0.85,
  );
  const chosen = candidates.sort((left, right) => right.width * right.height - left.width * left.height)[0];
  if (!chosen) throw new Error('Uncompressed page has no clear image object');
  return chosen;
}

/**
 * Reads one axis-aligned image from one uncompressed page into Document IR.
 * A compressed content stream or a rotated image is refused rather than dropped.
 */
export function importOnePdfImage(bytes: Uint8Array, pageNumber: number): QXPdfImageImport {
  if (!Number.isInteger(pageNumber) || pageNumber < 1) throw new Error('pageNumber must be a positive integer');
  const file = Uint8Array.from(bytes);
  const boxAt = indexOfBytes(file, bytesOf('/MediaBox'));
  if (boxAt < 0) throw new Error('PDF page has no MediaBox');
  const boxText = textOf(file, boxAt, boxAt + 120);
  const box = boxText.match(/\/MediaBox\s*\[\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*\]/);
  const pageWidth = Number(box?.[1] ?? 0);
  const pageHeight = Number(box?.[2] ?? 0);
  if (pageWidth <= 0 || pageHeight <= 0) throw new Error('PDF MediaBox is empty');
  const near = textOf(file, Math.max(0, boxAt - 800), Math.min(file.length, boxAt + 1500));
  const contentsId = Number(near.match(/\/Contents\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  if (!contentsId) throw new Error('PDF page has no content stream');
  const content = readStream(file, contentsId);
  if (/\/Filter\b/.test(content.dict)) throw new Error('PDF content stream is still compressed');
  if (!content.stream) throw new Error('PDF content stream is empty');
  const chosen = clearImage(placements(textOf(content.stream, 0, content.stream.length)), pageWidth, pageHeight);
  const resource = near.match(new RegExp(`\\/${chosen.name}\\s+(\\d+)\\s+0\\s+R`));
  const objectId = Number(resource?.[1] ?? 0);
  if (!objectId) throw new Error(`PDF image ${chosen.name} has no object`);
  const image = readStream(file, objectId);
  const head = image.stream;
  if (!head || head[0] !== 0xff || head[1] !== 0xd8 || head[2] !== 0xff) {
    throw new Error(`PDF object ${String(objectId)} is not a JPEG image`);
  }
  const jpeg = Uint8Array.from(head);
  const placed = {
    x: chosen.x,
    y: pageHeight - chosen.pdfY - chosen.height,
    width: chosen.width,
    height: chosen.height,
  };
  const sourceObjectId = `obj_${String(objectId)}`;
  const assetId = `asset_${String(objectId)}`;
  const src = `data:image/jpeg;base64,${base64(jpeg)}`;
  const document = parsePdfLayoutToIR({
    sourceHash: 'sha256:qxeditor-poc-object-mirror-01',
    pages: [
      {
        pageNumber,
        width: pageWidth,
        height: pageHeight,
        blocks: [
          {
            id: sourceObjectId,
            kind: 'image',
            assetId,
            text: src,
            bbox: placed,
          },
        ],
      },
    ],
  });
  return {
    document,
    jpeg,
    sourceObjectId,
    assetId,
    pageNumber,
    paintIndex: chosen.paintIndex,
    rotation: 0,
    placed,
  };
}
